import ServiceCollection from 'src/services/ServiceCollection';
import React, { useContext, useEffect, useMemo } from 'react';
import { Box, Button, ColumnLayout, Container } from '@amzn/awsui-components-react/polaris';
import 'src/components/CustomAppLayout.scss';
import 'src/components/custom-tables-shared/CustomTables.scss';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { GLOBAL_ACTIONS } from 'src/services/global/GlobalReducer';
import { ACTIONS, segmentTablesReducer } from 'src/services/segment-cdt/SegmentTablesReducer';
import { initialState } from 'src/services/segment-cdt/SegmentTablesState';
import { GlobalAppContext } from 'src/components/App';
import DynamicBreadcrumb from 'src/models/navigation/DynamicBreadcrumb';
import { useLocation } from 'react-router-dom';
import CONSTANTS from 'src/utils/constants';
import StringUtils from 'src/utils/stringUtils';
import { SegmentTablesProvider } from 'src/components/segment-cdt/SegmentTablesContext';
import SegmentTablesGrid from 'src/components/segment-cdt/list/SegmentTablesGrid';
import SegmentCreateWizard from 'src/components/segment-cdt/create/SegmentCreateWizard';
import { toSegmentTableListRow } from 'src/models/segment-cdt/SegmentTableListRow';
import { getPermissions } from 'src/components/AppPermissions';
import { AppModules } from 'src/models/permissions/RolePermissions';

/**
 * The Segment Data Tables tab: a read-only list of segment CDTs plus the Create entry point.
 * Provides `SegmentTablesContext` ({ state, dispatch, services }); the create wizard's nested children
 * consume it. Backed by ListSegmentDataTables (no data until that server op ships — see
 * SegmentDataTablesService). Reducer state is minimal: `refreshToken` (reload after create) + the
 * `createWizard` slice.
 */
export default function SegmentTablesView(props: { services: ServiceCollection }) {
    const { services } = props;
    const { globalDispatch } = useContext(GlobalAppContext);
    const [state, dispatch] = useReducerWithLogger(segmentTablesReducer, initialState);
    const { pathname } = useLocation();
    const { canAddNew } = getPermissions(AppModules.CUSTOM_DATA_TABLES);

    // Stable request; a new identity (on refreshToken bump) re-runs the list fetch. A create success
    // dispatches ACTIONS.REFRESH to reload the list without knowing what changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const listRequest = useMemo(() => ({}), [state.refreshToken]);
    const [listResponse, loading, error] = services.segmentDataTablesService.listSegmentDataTables(listRequest);

    const rows = useMemo(() => (listResponse?.tables ?? []).map(toSegmentTableListRow), [listResponse]);

    useEffect(() => {
        globalDispatch(GLOBAL_ACTIONS.SET_DYNAMIC_BREADCRUMB.withPayload(new DynamicBreadcrumb(
            pathname,
            CONSTANTS.PAGE_NAV.CUSTOM_TABLES.TITLE,
            CONSTANTS.PAGE_NAV.CUSTOM_TABLES.TITLE,
            CONSTANTS.PAGE_NAV.CUSTOM_TABLES.DESCRIPTION,
        )));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!StringUtils.isNullOrEmpty(error)) {
            services.messageService.showErrorBanner(error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    return (
        <SegmentTablesProvider services={services} state={state} dispatch={dispatch}>
            <Container data-class="containerWithHeaderAndGrid">
                <div className="customTableTableHeader">
                    <ColumnLayout columns={2} data-class="fullColumnLayout">
                        <Box variant="h2">
                            <span>Segment Data Tables </span>
                            <span className="customTablesRecordCounter">({rows.length})</span>
                        </Box>
                        <Box float="right">
                            {canAddNew &&
                                <Button variant="primary" onClick={() => dispatch(ACTIONS.OPEN_CREATE_WIZARD)}>Create Segment Table</Button>
                            }
                        </Box>
                    </ColumnLayout>
                </div>
                <div className="tableContainer">
                    <SegmentTablesGrid
                        loading={loading}
                        tables={rows}
                        tableDetailUrl={CONSTANTS.PAGE_NAV.SEGMENT_DATA_TABLE_DETAILS.URL}
                    />
                </div>
                <SegmentCreateWizard />
            </Container>
        </SegmentTablesProvider>
    );
}
